import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  MapPin, 
  Building2, 
  Target, 
  Award, 
  Calendar,
  FileText,
  TrendingUp,
  Users,
  ShoppingCart,
  Image as ImageIcon,
  Plus
} from 'lucide-react';

// Sample image URLs from Supabase storage
const projectImage1 = 'https://cwzpukwrlihylylmitqq.supabase.co/storage/v1/object/public/project-developer-images/project-developer-1.jpg';
const projectImage2 = 'https://cwzpukwrlihylylmitqq.supabase.co/storage/v1/object/public/project-developer-images/project-developer-2.jpg';
const projectImage3 = 'https://cwzpukwrlihylylmitqq.supabase.co/storage/v1/object/public/project-developer-images/project-developer-3.jpg';
const developerProfile = 'https://cwzpukwrlihylylmitqq.supabase.co/storage/v1/object/public/project-developer-images/developer-profile-1.jpg';

interface ProjectDeveloperDetailViewProps {
  project: any;
  onBack: () => void;
}

export const ProjectDeveloperDetailView = ({ project, onBack }: ProjectDeveloperDetailViewProps) => {
  const [selectedImages, setSelectedImages] = useState([
    projectImage1,
    projectImage2,
    projectImage3
  ]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setSelectedImages(prev => [...prev, e.target!.result as string]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handlePurchaseCredits = () => {
    console.log('Purchase credits for:', project.name);
    // TODO: Open purchase modal
  };

  // Sample data for enhanced project developer information
  const developerInfo = {
    name: project.developer || 'EcoGreen Solutions Ltd.',
    address: '123 Sustainability Ave, Green City, Brazil 12345',
    description: 'Leading environmental solutions provider specializing in reforestation and renewable energy projects across Latin America. Committed to creating lasting positive impact on climate change through innovative carbon credit projects.',
    goal: 'To offset 10 million tons of CO2 by 2030 through sustainable forest restoration and community-based environmental initiatives.',
    size: 'Large Scale (500+ employees, $50M+ annual revenue)',
    pastSuccesses: [
      'Successfully restored 50,000 hectares of Amazon rainforest',
      'Generated 2.5M verified carbon credits in 2023',
      'Partnered with 200+ local communities',
      'Achieved Gold Standard certification for all projects'
    ],
    auditProgram: 'Verra Verified Carbon Standard (VCS) with annual third-party audits by SGS and Bureau Veritas',
    otherInfo: 'ISO 14001 certified, member of IETA (International Emissions Trading Association), recipient of 2023 UN Global Compact Award for Environmental Excellence.'
  };

  const events = [
    { date: '2024-01-15', title: 'Project Registration Completed', description: 'Official registration with Verra VCS registry' },
    { date: '2024-02-20', title: 'Field Verification Audit', description: 'On-site verification by independent auditors' },
    { date: '2024-03-10', title: 'Community Engagement Workshop', description: 'Stakeholder consultation and community feedback session' },
    { date: '2024-04-05', title: 'Milestone Achievement', description: '25% of reforestation target completed' }
  ];

  const publications = [
    { title: 'Project Design Document (PDD)', date: '2023-12-01', type: 'PDF', size: '2.3 MB' },
    { title: 'Environmental Impact Assessment', date: '2024-01-15', type: 'PDF', size: '1.8 MB' },
    { title: 'Community Consultation Report', date: '2024-02-10', type: 'PDF', size: '950 KB' },
    { title: 'Biodiversity Monitoring Plan', date: '2024-03-01', type: 'PDF', size: '1.2 MB' }
  ];

  const statements = [
    { title: 'Verification Statement - Q1 2024', auditor: 'SGS International', date: '2024-04-01', status: 'Verified' },
    { title: 'Annual Monitoring Report', auditor: 'Bureau Veritas', date: '2024-03-15', status: 'Approved' },
    { title: 'Permanence Assessment', auditor: 'Verra Registry', date: '2024-02-28', status: 'Validated' }
  ];

  const milestones = [
    { phase: 'Project Planning', status: 'Completed', date: '2023-06-01', progress: 100 },
    { phase: 'Land Acquisition', status: 'Completed', date: '2023-09-15', progress: 100 },
    { phase: 'Initial Planting', status: 'Completed', date: '2024-01-01', progress: 100 },
    { phase: 'Growth Monitoring', status: 'In Progress', date: '2024-12-31', progress: 75 },
    { phase: 'First Harvest Credits', status: 'Planned', date: '2025-06-01', progress: 0 }
  ];

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Completed</Badge>;
      case 'in progress':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">In Progress</Badge>;
      case 'planned':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Planned</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">{status}</Badge>;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Projects
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">{project.name}</h1>
            <p className="text-muted-foreground">Project Developer Details</p>
          </div>
        </div>
        <Button onClick={handlePurchaseCredits} className="gap-2 bg-primary hover:bg-primary/90">
          <ShoppingCart className="w-4 h-4" />
          Purchase Issuer Credits
        </Button>
      </div>

      {/* Developer Profile Card */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-start gap-6">
            <img 
              src={developerProfile} 
              alt="Developer Profile" 
              className="w-24 h-24 rounded-full object-cover border-4 border-border"
            />
            <div className="flex-1">
              <CardTitle className="text-2xl mb-2">{developerInfo.name}</CardTitle>
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <MapPin className="w-4 h-4" />
                <span>{developerInfo.address}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground mb-4">
                <Building2 className="w-4 h-4" />
                <span>{developerInfo.size}</span>
              </div>
              <p className="text-sm text-muted-foreground">{developerInfo.description}</p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabs for detailed information */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
          <TabsTrigger value="publications">Publications</TabsTrigger>
          <TabsTrigger value="statements">Statements</TabsTrigger>
          <TabsTrigger value="milestones">Milestones</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Photos Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Project Photos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {selectedImages.map((image, index) => (
                  <div key={index} className="aspect-video rounded-lg overflow-hidden">
                    <img 
                      src={image} 
                      alt={`Project ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="image-upload"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <label htmlFor="image-upload">
                  <Button variant="outline" className="gap-2 cursor-pointer" asChild>
                    <span>
                      <Plus className="w-4 h-4" />
                      Add Photos
                    </span>
                  </Button>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Project Goals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{developerInfo.goal}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Past Successes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {developerInfo.pastSuccesses.map((success, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 flex-shrink-0" />
                      {success}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Audit Program
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{developerInfo.auditProgram}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Additional Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{developerInfo.otherInfo}</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          {events.map((event, index) => (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">{event.title}</h3>
                      <span className="text-sm text-muted-foreground">{event.date}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{event.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="publications" className="space-y-4">
          {publications.map((publication, index) => (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-primary" />
                    <div>
                      <h3 className="font-semibold">{publication.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {publication.type} • {publication.size} • {publication.date}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="statements" className="space-y-4">
          {statements.map((statement, index) => (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Award className="w-5 h-5 text-primary" />
                    <div>
                      <h3 className="font-semibold">{statement.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        By {statement.auditor} • {statement.date}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    {statement.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="milestones" className="space-y-4">
          {milestones.map((milestone, index) => (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    <div>
                      <h3 className="font-semibold">{milestone.phase}</h3>
                      <p className="text-sm text-muted-foreground">Target: {milestone.date}</p>
                    </div>
                  </div>
                  {getStatusBadge(milestone.status)}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${milestone.progress}%` }}
                  />
                </div>
                <p className="text-sm text-muted-foreground mt-2">{milestone.progress}% Complete</p>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};